import { Box, makeStyles, LabelValue, Typography } from '@dxb-web-ui-framework/design-system';
import React, { FC } from 'react';
import { TFunction } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { useTransactionTypes } from '../../../../transaction/src/contexts/Parameters/hooks';
import { PayrollDetails } from '../../domain/Payroll/entities/types';
import { useSelectedPayroll } from '../../contexts/PayrollFile/hooks';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  boxContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    border: `2px solid ${theme.palette.background.default}`,
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flex: 1,
      backgroundColor: '#F0F0F0',
    },
  },
  labelValueWrapper: {
    paddingRight: theme.spacing(2),
    paddingTop: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      paddingTop: theme.spacing(0),
    },
  },
}));

export interface BaseTransactionsDetailsProps {
  t: TFunction;
  payrollDetails: Nullable<PayrollDetails>;
  isLoading: boolean;
}

const BaseTransactionsDetails: FC<BaseTransactionsDetailsProps> = ({ t, payrollDetails, isLoading }) => {
  const classes = useStyles();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const selectedPayroll = useSelectedPayroll();
  const transactionTypes = useTransactionTypes();

  const payrollTransactionTypeName =
    transactionTypes?.find(transactionType => transactionType.id === payrollDetails?.transactionType)?.description ||
    payrollDetails?.transaction;

  return (
    <Box className={classes.root}>
      <Typography variant={'subtitle2'} color={'textPrimary'}>
        {t('payrollDetailsContent.baseTransactionDetails')}
      </Typography>
      <Box p={2} className={classes.boxContainer}>
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('payrollDetails.transactionUniqueId')}
            value={payrollDetails?.appTransactionUniqueId}
            valueAreaLabel={`${t('payrollDetails.transactionUniqueId')} ${payrollDetails?.appTransactionUniqueId}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('payrollDetails.transactionName')}
            value={payrollTransactionTypeName}
            valueAreaLabel={`${t('payrollDetails.transactionName')} ${payrollTransactionTypeName}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('payrollDetails.status')}
            value={payrollDetails?.status}
            valueAreaLabel={`${t('payrollDetails.status')} ${payrollDetails?.status}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
        <Box className={classes.labelValueWrapper}>
          <LabelValue
            label={t('payrollDetails.cancellationReason')}
            value={selectedPayroll?.cancellationReason || '-'}
            valueAreaLabel={`${t('payrollDetails.cancellationReason')} ${selectedPayroll?.cancellationReason || '-'}`}
            labelColor='textPrimary'
            labelSize='small'
            valueSize={isMobile ? 'small' : 'medium'}
            valueColor='primary'
            labelAlignment='left'
            valueAlignment='left'
            hasSkeleton={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export { BaseTransactionsDetails };
